/**
 * Trial API Service
 * Handles all CDI trial-related API requests
 *
 * Production Architecture:
 * - Frontend: Azure Container App (aca-fe-prd-cus)
 * - Custom Domain: www.videxa.ai (via Azure Front Door)
 * - Backend: Azure Container App with FastAPI
 * - Authentication: JWT tokens in Authorization header
 */

import axios from 'axios';

// API Base URL - uses environment variable or defaults to relative path for production
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Get auth token from localStorage
 * @returns {string|null} JWT token
 */
const getAuthToken = () => {
  return localStorage.getItem('auth_token');
};

/**
 * Create axios instance with default configuration
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout for production
});

/**
 * Add auth token to requests
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Handle response errors
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - redirect to login
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Get trial status for current user
 * @returns {Promise<Object>} Trial status data
 */
export const getTrialStatus = async () => {
  try {
    const response = await apiClient.get('/trial/status');
    return response.data;
  } catch (error) {
    console.error('Error getting trial status:', error);
    throw error;
  }
};

/**
 * Update trial progress
 * @param {number} stepCompleted - Step number completed (1-3)
 * @returns {Promise<Object>} Update response
 */
export const updateTrialProgress = async (stepCompleted) => {
  try {
    const response = await apiClient.post('/trial/progress', {
      step_completed: stepCompleted
    });
    return response.data;
  } catch (error) {
    console.error('Error updating trial progress:', error);
    throw error;
  }
};

/**
 * Start trial for new user
 * @returns {Promise<Object>} Start trial response
 */
export const startTrial = async () => {
  try {
    const response = await apiClient.post('/trial/start');
    return response.data;
  } catch (error) {
    console.error('Error starting trial:', error);
    throw error;
  }
};

/**
 * Share trial results via email
 * @param {Array<string>} recipientEmails - Email addresses to share with
 * @param {Array<string>} selectedReports - Report types to share
 * @param {string} customMessage - Optional custom message
 * @returns {Promise<Object>} Share response
 */
export const shareTrialResults = async (recipientEmails, selectedReports, customMessage = null) => {
  try {
    const response = await apiClient.post('/trial/share-results', {
      recipient_emails: recipientEmails,
      selected_reports: selectedReports,
      custom_message: customMessage
    });
    return response.data;
  } catch (error) {
    console.error('Error sharing trial results:', error);
    throw error;
  }
};

/**
 * Save trial settings (data retention and plan selection)
 * @param {string} dataRetention - Data retention preference
 * @param {string} selectedPlan - Selected plan (starter, professional, enterprise)
 * @returns {Promise<Object>} Save response
 */
export const saveTrialSettings = async (dataRetention, selectedPlan) => {
  try {
    const response = await apiClient.post('/trial/save-settings', {
      data_retention: dataRetention,
      selected_plan: selectedPlan
    });
    return response.data;
  } catch (error) {
    console.error('Error saving trial settings:', error);
    throw error;
  }
};

/**
 * Export trial API functions
 */
export const trialApi = {
  getTrialStatus,
  updateTrialProgress,
  startTrial,
  shareTrialResults,
  saveTrialSettings,
};

export default trialApi;
